/**
 * Add machine, Cloud VM (ADR 0105, ADR 0106 decisions 3, 3a and 4): a VM in a cloud account
 * joins with the agent already on it. Two ways, one primary:
 *
 *   Create with AWS    where the server runs module 'cloud' (Orgus Cloud): Connect AWS once (owner
 *                      or admin), then name, size, lifetime and Create VM. Region and repository
 *                      wait behind More options.
 *   Any other cloud    the cloud-init user data (CloudVmPanel), folded under "Another cloud" when
 *                      AWS is offered, else the tab's only way (a local hub has no module 'cloud'),
 *                      with the providers still to come named below it.
 *
 * A VM left unnamed gets a memorable name (memorableMachineName), never one the fleet has; the
 * create form starts with one, and it becomes the EC2 Name tag too.
 */
import { useMemo } from 'react';
import { memorableMachineName } from '@orgus/protocol';
import { Disclosure } from '@orgus/ui';
import { Cloud, Sparkles } from '@orgus/ui/icons';
import { useOrgus } from '../../lib/store.ts';
import { CreateWithAwsView } from '../cloud/CreateWithAws.tsx';
import { useCloud } from '../cloud/data.ts';
import type { CloudView } from '../cloud/data.ts';
import { CloudVmPanel } from './CloudVmPanel.tsx';
import type { JoinTicketsState } from './useJoinTickets.ts';

/** The providers the one-click path reaches after AWS, in the order they come (ADR 0106 decision 3). */
export const CLOUD_PROVIDERS_NEXT = ['Azure', 'Google Cloud', 'Hetzner', 'DigitalOcean', 'Oracle Cloud'] as const;

type CloudState = Pick<JoinTicketsState, 'on' | 'cloud' | 'makeCloud' | 'copyCloud'>;

/** The panel's actions with a memorable name filled in when the person typed none. */
export function withMemorableName(s: CloudState, taken: readonly string[], name: () => string = () => memorableMachineName(taken)): CloudState {
  return { ...s, makeCloud: (o) => s.makeCloud({ ...o, name: o.name?.trim() || name() }) };
}

/** True when this server can create AWS VMs (or is still saying whether it can). */
export function awsOffered(cloud: Pick<CloudView, 'phase' | 'state'>): boolean {
  if (cloud.phase === 'missing' || cloud.phase === 'error') return false;
  return cloud.phase === 'loading' || !!cloud.state?.provisioner.ready;
}

export function CloudVmTab({ s, hubUrl }: { s: CloudState; hubUrl?: string }) {
  const machines = useOrgus((st) => st.machines);
  const cloud = useCloud();
  const vms = cloud.state?.vms;
  const taken = useMemo(() => [...Object.values(machines).flatMap((m) => [m.name, m.hostname].filter((n): n is string => !!n)), ...(vms ?? []).map((v) => v.name)], [machines, vms]);
  return <CloudVmTabView s={s} cloud={cloud} taken={taken} {...(hubUrl ? { hubUrl } : {})} />;
}

export function CloudVmTabView({ s, hubUrl, cloud, taken, suggest = memorableMachineName }: { s: CloudState; hubUrl?: string; cloud: CloudView; taken: readonly string[]; suggest?: (taken: readonly string[]) => string }) {
  const named = useMemo(() => withMemorableName(s, taken, () => suggest(taken)), [s, taken, suggest]);
  const panel = <CloudVmPanel s={named} {...(hubUrl ? { hubUrl } : {})} />;
  if (!awsOffered(cloud)) {
    return (
      <div className="flex flex-col gap-4" data-testid="cloud-vm-tab">
        {panel}
        <section aria-label="Orgus creates the VM" className="flex items-start gap-3 rounded-card border border-border-subtle p-4" data-testid="cloud-vm-next">
          <Sparkles className="mt-1 size-4 shrink-0 text-fg-subtle" aria-hidden />
          <div className="flex min-w-0 flex-col gap-1">
            <p className="text-ui font-medium text-fg">Coming next: Orgus creates the VM</p>
            <p className="text-caption text-fg-subtle">{cloud.phase === 'ready' && cloud.state?.provisioner.problem ? cloud.state.provisioner.problem : ['AWS', ...CLOUD_PROVIDERS_NEXT].join(', ')}</p>
          </div>
        </section>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4" data-testid="cloud-vm-tab">
      <CreateWithAwsView cloud={cloud} suggestName={() => suggest(taken)} />
      {s.on && (
        <Disclosure icon={<Cloud className="size-4" aria-hidden />} title="Another cloud" summary={`Paste cloud-init into ${CLOUD_PROVIDERS_NEXT.slice(0, 3).join(', ')} or any VM form`} className="border-t border-border-subtle pt-2">
          {panel}
        </Disclosure>
      )}
    </div>
  );
}
